import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "../auth";
import { queryConfigs } from "@/lib/queryClient";
import { addMinutes, parseISO, startOfDay, endOfDay } from "date-fns";
import type {
  CalendarEvent,
  CalendarEventType,
  CalendarEventSource,
  CalendarEventParticipant,
} from "@/types/calendar-view";
import { parseActivityCustomData, resolveMeetingJoinUrl } from "@/lib/activityCustomData";

// ---------------------------------------------------------------------------
// useCalendarViewEvents
// Bridges the `activities` table + `calendar_events` table → CalendarEvent[]
// for all calendar components. Merges CRM activities with Google Calendar
// events into a single sorted array, with deduplication for linked events.
// ---------------------------------------------------------------------------

const DEFAULT_DURATIONS: Record<string, number> = {
  call: 30,
  meeting: 60,
  email: 15,
  task: 45,
  proposal: 30,
  note: 15,
};

export interface UseCalendarViewEventsParams {
  userId?: string;
  startDate: Date;
  endDate: Date;
  types?: string[];
}

function buildParticipantsFromActivity(activity: any): CalendarEventParticipant[] {
  const list: CalendarEventParticipant[] = [];
  const seen = new Set<string>();
  const add = (email: string | null | undefined, name?: string | null, status?: string | null) => {
    if (!email || typeof email !== "string") return;
    const e = email.trim().toLowerCase();
    if (!e || seen.has(e)) return;
    seen.add(e);
    list.push({ email, name: name || null, status: status ?? null });
  };

  if (activity.owner?.email) {
    add(activity.owner.email, activity.owner.full_name, "Organizador");
  }
  if (activity.leads?.email) {
    add(activity.leads.email, activity.leads.name, null);
  }
  const cd = parseActivityCustomData(activity.custom_data);
  const raw = cd.participants;
  if (Array.isArray(raw)) {
    for (const p of raw) {
      if (p && typeof p === "object" && "email" in p) {
        const email = (p as { email?: string }).email;
        const name = (p as { name?: string | null }).name;
        const status = (p as { status?: string | null }).status;
        add(
          email,
          typeof name === "string" ? name : null,
          typeof status === "string" ? status : null
        );
      }
    }
  }
  return list;
}

function activityToCalendarEvent(activity: any): CalendarEvent {
  const start = parseISO(activity.date);
  const duration = activity.duration_minutes ?? DEFAULT_DURATIONS[activity.type] ?? 30;
  const end = addMinutes(start, duration);
  const meet = resolveMeetingJoinUrl(activity);
  return {
    id: activity.id,
    title: activity.title,
    start,
    end,
    allDay: false,
    type: (activity.type as CalendarEventType) || "task",
    status: activity.status === "cancelled" ? "canceled" : activity.status,
    source: "crm",
    meta: {
      activityId: activity.id,
      leadId: activity.leads?.id,
      leadName: activity.leads?.name,
      dealId: activity.deals?.id,
      dealTitle: activity.deals?.title,
      companyId: activity.companies?.id ?? activity.leads?.companies?.id,
      companyName: activity.companies?.name ?? activity.leads?.companies?.name,
      assignedTo: activity.owner_id ?? activity.created_by,
      assigneeName: activity.owner?.full_name,
      description: activity.description ?? null,
      meetLink: meet,
      conferenceUrl: meet ?? undefined,
      participants: buildParticipantsFromActivity(activity),
    },
  };
}

function normalizeGoogleParticipants(row: any): CalendarEventParticipant[] {
  const raw = row.participants;
  if (!raw || !Array.isArray(raw)) return [];
  return raw
    .map((p: unknown) => {
      if (!p || typeof p !== "object") return null;
      const o = p as { email?: string; name?: string | null; status?: string | null };
      if (!o.email) return null;
      return { email: o.email, name: o.name ?? null, status: o.status ?? null };
    })
    .filter(Boolean) as CalendarEventParticipant[];
}

function externalEventToCalendarEvent(row: any): CalendarEvent {
  const conf = row.conference_url ?? null;
  return {
    id: `gcal-${row.id}`,
    title: row.title || "(Sem título)",
    start: parseISO(row.start_time),
    end: parseISO(row.end_time),
    allDay: row.all_day ?? false,
    type: "external" as CalendarEventType,
    status: "pending",
    source: "google_calendar" as CalendarEventSource,
    meta: {
      activityId: row.activity_id ?? undefined,
      externalEventId: row.google_event_id ?? row.nylas_event_id,
      conferenceUrl: conf ?? undefined,
      meetLink: conf,
      htmlLink: row.html_link ?? undefined,
      description: row.description ?? null,
      participants: normalizeGoogleParticipants(row),
    },
  };
}

export function useCalendarViewEvents({
  userId,
  startDate,
  endDate,
  types,
}: UseCalendarViewEventsParams) {
  const { orgId } = useCurrentOrgId();

  const startISO = startOfDay(startDate).toISOString();
  const endISO = endOfDay(endDate).toISOString();

  return useQuery<CalendarEvent[]>({
    queryKey: ["calendar-view-events", orgId, userId, startISO, endISO, types],
    queryFn: async () => {
      if (!orgId) throw new Error("Organization ID not found");

      let query = supabase
        .from("activities")
        .select(`
          *,
          leads(id, name, email, phone, company_id, companies(id, name)),
          companies(id, name),
          deals(id, title)
        `)
        .eq("org_id", orgId)
        .gte("date", startISO)
        .lte("date", endISO)
        .order("date", { ascending: true });

      if (userId) {
        // Agenda = atividades das quais o usuário é DONO (owner_id).
        // Não incluir created_by: traz atividades de outros donos que o usuário
        // apenas criou (ex.: toques de cadência despachados em nome dele).
        query = query.eq("owner_id", userId);
      }

      if (types && types.length > 0) {
        query = query.in("type", types as never[]);
      }

      const { data, error } = await query;
      if (error) throw error;

      const activities = (data ?? []) as any[];

      // Fetch owner profiles
      const ownerIds = [
        ...new Set(activities.filter((a) => a.owner_id).map((a) => a.owner_id)),
      ];

      let ownersMap: Record<string, { user_id: string; email: string; full_name: string }> = {};

      if (ownerIds.length > 0) {
        const { data: owners } = await supabase
          .from("profiles")
          .select("user_id, email, full_name")
          .in("user_id", ownerIds);

        if (owners) {
          ownersMap = Object.fromEntries(owners.map((o) => [o.user_id, o]));
        }
      }

      const crmEvents = activities
        .filter((a) => !(a.type === 'email' && a.custom_data?.inbound === true))
        .map((a) => {
          const enriched = { ...a, owner: a.owner_id ? ownersMap[a.owner_id] : null };
          return activityToCalendarEvent(enriched);
        });

      // Fetch external calendar events (Google Calendar)
      let externalQuery = supabase
        .from("calendar_events")
        .select("*")
        .eq("org_id", orgId)
        .gte("start_time", startISO)
        .lte("start_time", endISO)
        .neq("status", "cancelled");

      if (userId) {
        externalQuery = externalQuery.eq("user_id", userId);
      }

      const { data: externalData } = await externalQuery;

      const googleEvents = (externalData ?? []).map(externalEventToCalendarEvent);

      // Dedup: keep CRM activities, only show external events that have no linked activity
      const externalOnly = googleEvents.filter((e) => !e.meta?.activityId);

      return [...crmEvents, ...externalOnly].sort(
        (a, b) => a.start.getTime() - b.start.getTime(),
      );
    },
    enabled: !!orgId,
    ...queryConfigs.crm,
  });
}
